/**
 * Handles all functionality related to account navigation
 *
 * A11Y requirements: {@link https://w3c.github.io/aria-practices/examples/menu-button/menu-button-links.html}
 */

import ClassNames from "./classNames.js";
import DomElements from "./domElements.js";
const {
  $navPrimarySubNavs: $subNavs,
  $navPrimaryContainer,
  $menuButtonMobile,
  $navContainerWrapper
} = DomElements;

import { interpretAction } from "../../@helpers/js/interpretAction.js";

/**
 * Check if mobile menu open
 *
 * @returns 'true' if open, 'false' if hidden
 */
const isMobileMenuOpen = () => !!$menuButtonMobile?.classList?.contains(
  `${ClassNames.layout.menuButtonMobile}--${ClassNames.modifiers.active}`
);

// Currently active
let activeSubmenuId = "";

const updateActiveSubmenuId = (newValue) => activeSubmenuId = newValue
const getActiveSubmenuId = () => activeSubmenuId

/**
 * Close all submenus
 */
function closeAllSubmenus() {
  const subNavList = Array.prototype.slice.call($subNavs, 0);
  subNavList.forEach(($submenu) => {
    const { submenuId } = $submenu.dataset;
    submenuId && toggle(submenuId, "close");
  });
  activeSubmenuId = "";
}

/**
 * Show, hide or toggle a submenu
 *
 * Also calculates the submenu position for tablet and up
 * (unable to do with just CSS)
 *
 * @param id Submenu id
 * @param action Type of action
 */
const toggle = (id, action) => {
  const subNavList = Array.prototype.slice.call($subNavs, 0);
  subNavList.forEach(($submenu) => {
    if ($submenu.dataset.submenuId === id) {
      // Toggle submenu
      $submenu.classList[interpretAction(action)](
        `${ClassNames.navPrimary.subNav}--${ClassNames.modifiers.active}`
      );

      // Set active submenu state on trigger
      $submenu.parentElement
        .querySelector(`.${ClassNames.navPrimary.mainItem}`)
        .classList[interpretAction(action)](
          `${ClassNames.navPrimary.mainItem}--${ClassNames.modifiers.submenu}`
        );

      $submenu.parentElement.classList[interpretAction(action)](
        `${ClassNames.navPrimary.listItem}--${ClassNames.modifiers.submenu}`
      );

      // Set active submenu state on container
      // (Required to handle different active states in CSS)
      if (window.innerWidth >= 768) {
        $navPrimaryContainer.classList[interpretAction(action)](
          `${ClassNames.navPrimary.container}--${ClassNames.modifiers.submenu}`
        );
      } else {
        $navPrimaryContainer.classList.remove(
          `${ClassNames.navPrimary.container}--${ClassNames.modifiers.submenu}`
        );
      }

      // Handle `aria-expanded`
      const trigger = document.querySelector(`[data-submenu-id="${id}"]`);

      if (trigger) {
        trigger.setAttribute(
          "aria-expanded",
          `${!(trigger.getAttribute("aria-expanded") === "true")}`
        );
      }

      // Set submenu position (by rules of design)
      const menuWidth =
        $navContainerWrapper.clientWidth -
        parseFloat(getComputedStyle($navContainerWrapper).paddingLeft) -
        parseFloat(getComputedStyle($navContainerWrapper).paddingRight);
      // Number of pixels the submenu is too far overlapping to the right
      const overlap =
        $submenu.clientWidth -
        2 * (window.innerWidth >= 1024 ? 32 : 24) -
        (menuWidth - $submenu.parentElement.offsetLeft);

      // Correct position for screens equal or larger the 1024 (M up)
      if (window.innerWidth >= 1024) {
        if (overlap < $submenu.parentElement.offsetLeft) {
          $submenu.style.marginLeft = `-${overlap}px`;
        } else {
          $submenu.style.marginLeft = `-${$submenu.parentElement.offsetLeft}px`;
        }
      }

      // Correct position for breakpoint 768 - 1024 (M)
      if (window.innerWidth >= 768 && window.innerWidth < 1024) {
        if ($submenu.dataset.columns <= 2) {
          $submenu.style.marginLeft = `-${overlap}px`;
        } else {
          const spaceLeft =
            $navContainerWrapper.offsetLeft + $submenu.parentElement.offsetLeft;
          $submenu.style.marginLeft = `-${spaceLeft}px`;
        }
      }

      // Reset corrected position for screens smaller then 768 (S down)
      if (window.innerWidth < 768) $submenu.style.marginLeft = "";
    }
  });
};

/**
 * Check if any submenu is open
 *
 * @returns If open
 */
const isAnySubmenuOpen = () => !!activeSubmenuId;

export default {
  isMobileMenuOpen,
  updateActiveSubmenuId,
  getActiveSubmenuId,
  closeAllSubmenus,
  toggle,
  isAnySubmenuOpen,
};
